import { useEffect, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, Film, Gamepad2, Music2, Play, Shield, Sparkles, Tv, Users } from 'lucide-react';
import { playDestination } from '../services/playEntry';
import type { SessionCategory } from '../services/questionSession';
import { ComicBurst } from './PopJuice';
import { nextWarmupQuestion, rememberWarmupQuestion } from '../services/homeWarmup';

const universes = [
  { category: 'movies', icon: Film, color: '#FFC46B', background: '#352517' },
  { category: 'videogames', icon: Gamepad2, color: '#72E4F4', background: '#16343F' },
  { category: 'series', icon: Tv, color: '#BF91FF', background: '#382252' },
  { category: 'anime', icon: Sparkles, color: '#FF84BC', background: '#46223C' },
  { category: 'comics', icon: Shield, color: '#FFD74A', background: '#403519' },
  { category: 'music', icon: Music2, color: '#71E4BD', background: '#153C35' },
] satisfies { category: SessionCategory; icon: typeof Film; color: string; background: string }[];

export function PopBrand() {
  return <span className="qpug-brand"><span className="qpug-brand-mark" aria-hidden="true">?</span><span>question<br />pour un <b>geek.</b></span></span>;
}

export function HomeEntryActions({ authenticated }: { authenticated: boolean }) {
  const { t } = useTranslation();
  return <div className="qpug-entry">
    <div className="qpug-hero-actions">
      <Link className="qpug-button qpug-yellow" to={playDestination(authenticated)}><Play aria-hidden="true" size={20} />{t(authenticated ? 'pop.soloAction' : 'pop.playAction')}</Link>
      <Link className="qpug-button qpug-outline" to="/games"><Users aria-hidden="true" size={20} />{t('pop.friendsAction')}</Link>
    </div>
    <p className="qpug-game-promise"><Check size={15} aria-hidden="true" />{t(authenticated ? 'pop.savedSolo' : 'pop.discoveryPromise')}<span>{t(authenticated ? 'pop.serverProgress' : 'pop.noAccount')}</span></p>
  </div>;
}

export function UniverseLinks({ authenticated }: { authenticated: boolean }) {
  const { t } = useTranslation();
  return <div className="qpug-universe-list">{universes.map(({ category, icon: Icon, color, background }) =>
    <Link key={category} className="qpug-universe" to={playDestination(authenticated, category)} style={{ '--universe-ink': color, '--universe-tile': background } as CSSProperties}>
      <Icon aria-hidden="true" /><span>{t(`pop.categories.${category}`)}</span>
    </Link>
  )}</div>;
}

export function HomeWarmup() {
  const { t, i18n } = useTranslation();
  const [round, setRound] = useState(() => ({ question: nextWarmupQuestion(), answer: null as number | null }));
  const { question, answer } = round;
  const copy = question[i18n.resolvedLanguage?.startsWith('en') ? 'en' : 'fr'];
  const CategoryIcon = universes.find(item => item.category === question.category)!.icon;
  const correct = answer === question.correctIndex;

  useEffect(() => rememberWarmupQuestion(question.id), [question.id]);
  useEffect(() => {
    const revisit = (event: PageTransitionEvent) => {
      if (event.persisted) setRound(current => ({ question: nextWarmupQuestion(current.question.id), answer: null }));
    };
    window.addEventListener('pageshow', revisit);
    return () => window.removeEventListener('pageshow', revisit);
  }, []);

  return <section className={`qpug-hero-quiz ${correct ? 'has-success' : answer !== null ? 'has-miss' : ''}`} aria-label={t('pop.warmupLabel')}>
    {correct && <ComicBurst />}
    <span className="qpug-quiz-sticker">{t('pop.yourTurn')}</span>
    <div className="qpug-sample-top"><span><CategoryIcon size={20} aria-hidden="true" />{t(`pop.categories.${question.category}`)}</span><span>{t('pop.warmupLabel')}</span></div>
    <h2>{copy.lines[0]}<br />{copy.lines[1]}<br /><span>{copy.lines[2]}</span></h2>
    <div className="qpug-sample-answers" role="group" aria-label={t('pop.chooseAnswer')}>
      {copy.options.map((option, index) => <button key={index} type="button" disabled={answer !== null}
        className={answer !== null && index === question.correctIndex ? 'is-correct' : answer === index ? 'is-wrong' : ''}
        onClick={() => setRound(current => current.answer === null ? { ...current, answer: index } : current)}><b aria-hidden="true">{String.fromCharCode(65 + index)}</b><span>{option}</span>{answer !== null && index === question.correctIndex && <Check size={18} aria-label={t('pop.correctAnswer')} />}</button>)}
    </div>
    <p className="qpug-sample-feedback" role="status">{t(answer === null ? 'pop.warmupHint' : correct ? 'pop.warmupSuccess' : 'pop.warmupCorrection', { answer: copy.options[question.correctIndex] })}</p>
    <span className="qpug-quiz-notch" aria-hidden="true">?</span>
  </section>;
}

export function PopFooter() {
  const { t } = useTranslation();
  return <footer className="qpug-footer qpug-wrap"><Link to="/">Question pour un Geek</Link><span>{t('pop.footer')}</span><a href="https://bluehoodgames.com" target="_blank" rel="noopener noreferrer">Bluehood Games <ArrowRight size={13} aria-hidden="true" /></a></footer>;
}
